import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/formatters';
import socket from '../services/socket';

// Chart rendering imports
import { API_BASE_URL } from '../config';  

import { LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';

const BalanceHistoryChart = ({ userId, isPro, currentTotalValue, initialBalance = 10000 }) => {
  const isMounted = useRef(true);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [trades, setTrades] = useState([]);
  const [timeRange, setTimeRange] = useState('1h'); // Default to 1 hour view
  const [interval, setInterval] = useState('5m'); // Default to 5-minute intervals
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changePercent, setChangePercent] = useState(0);
  const [changeValue, setChangeValue] = useState(0);
  const [isPositiveChange, setIsPositiveChange] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch initial balance history data
  useEffect(() => {
    // Set mounted flag
    isMounted.current = true;
    
    const fetchBalanceHistory = async () => {
      try {
        if (!userId) return;
        
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/balance-history/${userId}?timeRange=${timeRange}&interval=${interval}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        
        const data = await response.json();
        
        // Only update state if component is still mounted
        if (!isMounted.current) return;
        
        const formattedHistory = formatBalanceHistoryData(data.balanceHistory || []);
        
        // Calculate change from initial balance (overall P/L)
        if (formattedHistory.length > 0) {
          const lastValue = currentTotalValue || formattedHistory[formattedHistory.length - 1].totalBalance;
          const base = initialBalance || 10000;
          const difference = lastValue - base;

          setChangeValue(difference);
          setChangePercent((difference / base * 100).toFixed(2));
          setIsPositiveChange(difference >= 0);
        }
        
        setBalanceHistory(formattedHistory);
        setTrades(data.trades || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching balance history:', error);
        if (isMounted.current) {
          setError('Failed to load balance history');
          setLoading(false);
        }
      }
    };
    
    fetchBalanceHistory();
    
    // Clean up when component unmounts
    return () => {
      isMounted.current = false;
    };
  }, [userId, timeRange, interval]); // Re-fetch when userId, timeRange or interval changes
  
  // Socket updates - Keep this minimal to avoid triggering re-renders
  useEffect(() => {
    const handleBalanceUpdate = (data) => {
      // Only update if a balance update includes history data
      if (data && data.balanceHistory) {
        const formattedHistory = formatBalanceHistoryData(data.balanceHistory);
        setBalanceHistory(formattedHistory);
        
        // Recalculate change from initial balance (overall P/L)
        if (formattedHistory.length > 0) {
          const lastValue = formattedHistory[formattedHistory.length - 1].totalBalance;
          const base = initialBalance || 10000;
          const difference = lastValue - base;

          setChangeValue(difference);
          setChangePercent((difference / base * 100).toFixed(2));
          setIsPositiveChange(difference >= 0);
        }
      }
    };
    
    // Subscribe to socket events
    if (socket && socket.on) {
      socket.on('balance-history-update', handleBalanceUpdate);
    }
    
    // Clean up socket listeners on unmount
    return () => {
      if (socket && socket.off) {
        socket.off('balance-history-update', handleBalanceUpdate);
      }
    };
  }, []);  // Empty dependency array - only run once
  
  // Process balance history data for charting
  const formatBalanceHistoryData = (data) => {
    return data.map(item => ({
      ...item,
      // Convert timestamp to Date object or readable format
      time: new Date(item.timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit'
      }),
      date: new Date(item.timestamp).toLocaleDateString(),
      shortDate: new Date(item.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      fullTimestamp: new Date(item.timestamp),
      // Ensure numeric values
      totalBalance: parseFloat(item.total_balance),
      cashBalance: parseFloat(item.cash_balance),
      positionsValue: parseFloat(item.positions_value),
    }));
  };
  
  // Handle time range change
  const handleRangeChange = (newRange) => {
    setTimeRange(newRange);
    
    // Reset interval for shorter timeframes
    if (newRange === '5m' || newRange === '15m' || newRange === '30m') {
      setInterval('1m');
    } else if (newRange === '1h') {
      setInterval('5m');
    } else if (newRange === '1d') {
      setInterval('15m');
    } else if (newRange === '3d') {
      setInterval('1h');
    }
  };
  
  // Handle interval changes
  const handleIntervalChange = (newInterval) => {
    setInterval(newInterval);
  };
  
  // Determine if interval selector should be shown based on timeRange
  const showIntervalSelector = timeRange === '1h' || timeRange === '1d' || timeRange === '3d';
  
  // Render interval selector
  const renderIntervalSelector = () => {
    if (!showIntervalSelector) return null;
    
    // Determine available intervals based on timeRange
    let availableIntervals = [];
    
    if (timeRange === '1h') {
      availableIntervals = ['1m', '5m'];
    } else if (timeRange === '1d') {
      availableIntervals = ['5m', '15m', '30m', '1h'];
    } else if (timeRange === '3d') {
      availableIntervals = ['15m', '1h', '4h'];
    }
    
    return (
      <div className="interval-selector">
        <span className="interval-label">Interval:</span>
        {availableIntervals.map(int => (
          <button 
            key={int}
            className={`interval-btn ${interval === int ? 'active' : ''}`}
            onClick={() => handleIntervalChange(int)}
          >
            {int.toUpperCase()}
          </button>
        ))}
      </div>
    );
  };

  // Custom tooltip component for more Robinhood-like appearance
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const timeDisplay = timeRange === '5m' || timeRange === '15m' || timeRange === '30m' || timeRange === '1h' 
        ? data.time 
        : data.shortDate;
        
      return (
        <div className="custom-tooltip">
          <p className="tooltip-date">{timeDisplay}</p>
          <p className="tooltip-value">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
  
    return null;
  };
  
  if (loading) {
    return (
      <div className="balance-history-loading">
        <div className="spinner-sm"></div>
        <p>Loading balance history...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="balance-history-error">
        <p>Error: {error}</p>
      </div>
    );
  }
  
  if (balanceHistory.length === 0) {
    return (
      <div className="balance-history-empty">
        <h3>Balance History</h3>
        <p>No balance history available. Start trading to see your performance over time!</p>
      </div>
    );
  }

  // Find min and max values for better Y-axis scaling
  const values = balanceHistory.map(point => point.totalBalance);
  const minBalance = values.length > 0 ? Math.min(...values) * 0.995 : 0; // 0.5% padding
  const maxBalance = values.length > 0 ? Math.max(...values) * 1.005 : 0; // 0.5% padding

  // Get primary color based on price movement and theme
  // Using neon green/red for terminal theme
  const getPrimaryColor = () => {
    return isPositiveChange ? '#00ff41' : '#ff4444';
  };
  
  return (
    <div className={`balance-history-chart ${isPro ? 'pro-mode' : ''}`}>
      <div className="chart-header">
        <div className="balance-info">
          <h3>Portfolio Value</h3>
          <div className="current-balance">
            {formatCurrency(currentTotalValue || balanceHistory[balanceHistory.length - 1].totalBalance)}
          </div>
          <div className={`change-info ${isPositiveChange ? 'positive' : 'negative'}`}>
            <span className="change-value">{isPositiveChange ? '+' : ''}{formatCurrency(changeValue)}</span>
            <span className="change-percent">({isPositiveChange ? '+' : ''}{changePercent}%)</span>
            <span className="period-label">ALL</span>
          </div>
        </div>
        
        <div className="time-controls">
          <div className="time-range-selector">
            <button 
              className={`period-btn ${timeRange === '5m' ? 'active' : ''}`}
              onClick={() => handleRangeChange('5m')}
            >
              5M
            </button>
            <button 
              className={`period-btn ${timeRange === '15m' ? 'active' : ''}`}
              onClick={() => handleRangeChange('15m')}
            >
              15M
            </button>
            <button 
              className={`period-btn ${timeRange === '30m' ? 'active' : ''}`}
              onClick={() => handleRangeChange('30m')}
            >
              30M
            </button>
            <button
              className={`period-btn ${timeRange === '1h' ? 'active' : ''}`}
              onClick={() => handleRangeChange('1h')}
            >
              1H
            </button>
          </div>
        </div>
      </div>
      
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
          <AreaChart
            data={balanceHistory}
            margin={isMobile
              ? { top: 5, right: 5, left: 5, bottom: 5 }
              : { top: 5, right: 10, left: 10, bottom: 5 }
            }
          >
            <defs>
              <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={getPrimaryColor()} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={getPrimaryColor()} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0, 255, 65, 0.1)" />
            <XAxis
              dataKey={timeRange === '5m' || timeRange === '15m' || timeRange === '30m' || timeRange === '1h' ? 'time' : 'shortDate'}
              tick={{ fontSize: isMobile ? 9 : 12, fill: 'rgba(0, 255, 65, 0.6)' }}
              tickLine={false}
              axisLine={false}
              minTickGap={isMobile ? 40 : 30}
            />
            <YAxis
              tick={{ fontSize: isMobile ? 9 : 12, fill: 'rgba(0, 255, 65, 0.6)' }}
              tickCount={isMobile ? 4 : 5}
              domain={[minBalance, maxBalance]}
              tickFormatter={(value) => isMobile
                ? (value >= 1000 ? `$${(value/1000).toFixed(1)}k` : `$${value.toFixed(0)}`)
                : formatCurrency(value)
              }
              axisLine={false}
              tickLine={false}
              width={isMobile ? 45 : 80}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="totalBalance"
              name="Portfolio Value"
              stroke={getPrimaryColor()}
              fillOpacity={1}
              fill="url(#colorBalance)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {/* Statistics Section */}
      {balanceHistory.length > 0 && (
        <div className="price-statistics">
          <div className="stat-box">
            <span className="stat-label">Open</span>
            <span className="stat-value">{formatCurrency(balanceHistory[0]?.totalBalance || 0)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">High</span>
            <span className="stat-value">{formatCurrency(maxBalance || 0)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Low</span>
            <span className="stat-value">{formatCurrency(minBalance || 0)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Current</span>
            <span className="stat-value">{formatCurrency(currentTotalValue || balanceHistory[balanceHistory.length - 1]?.totalBalance || 0)}</span>
          </div>
        </div>
      )}
      
      {/* Optional: Trade markers or summary */}
      {/* {trades.length > 0 && (
        <div className="trade-markers">
          <h4>Recent Trades</h4>
          <div className="trades-list">
            {trades.slice(0, 3).map(trade => (
              <div key={trade.id} className={`trade-item ${trade.type.toLowerCase()}`}>
                <div className="trade-header">
                  <span className="ticker">{trade.ticker}</span>
                  <span className="type">{trade.type}</span>
                </div>
                <div className="trade-details">
                  <span className="trade-shares">{trade.shares} shares</span>
                  <span className="trade-price">{formatCurrency(trade.price)}</span>
                </div>
                <div className="trade-time">
                  {new Date(trade.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
            {trades.length > 3 && (
              <div className="more-trades">
                View all {trades.length} trades
              </div>
            )}
          </div>
        </div>
      )} */}
    </div>
  );
};

export default BalanceHistoryChart;